import sharedConstants from "@lib/shared_constants";

const { PROVIDERS } = sharedConstants.AI_SETTINGS;

/**
 * The minimal shape of the agents-models lookup needed to resolve a raw model id to its
 * display name. Declared structurally (rather than importing the `@dashboardApi` type)
 * so this `@lib` helper stays free of `@dashboardApi` dependencies; the real
 * `GenerativeAiAgentsModelsResult` passed by callers is structurally compatible.
 */
export interface ModelsLookup {
  results: ReadonlyArray<{
    code: number;
    models: ReadonlyArray<{ name: string; model: string }>;
  }>;
}

/**
 * The single logical model choice a user makes: which provider, which model, and at
 * what thinking level. Captured together (rather than as three separate fields) because
 * the user thinks of it as one decision. `provider`/`model`/`thinking_level` are the raw
 * stored values (provider code, raw model id, thinking level string).
 */
export interface LogicalModel {
  provider?: number;
  model?: string | null;
  thinking_level?: string | null;
}

interface FormatLogicalModelLabelOptions {
  /** thinking level labels keyed by thinking level (e.g. { high: "High" }) */
  thinkingLevelLabels: Record<string, string>;
  /** label to show for the Braze Auto (provider BRAZE, model "auto") special case */
  brazeAutoLabel: string;
}

/**
 * Turns a {@link LogicalModel} into the single display label shown wherever the model
 * choice is presented read-only — the agent details summary and the version changelog.
 * Resolves the raw model id to its display name via `modelsData`, applies the Braze Auto
 * special case, and appends the thinking level (when set and not "none") joined with a
 * middle dot, e.g. "Claude Sonnet 4.5 · High", "GPT-5.4", or "Braze Auto". Falls back to
 * the raw model id when it can't be resolved (or to "" when there is no model).
 */
export const formatLogicalModelLabel = (
  { provider, model, thinking_level: thinkingLevel }: LogicalModel,
  modelsData: ModelsLookup | undefined,
  { thinkingLevelLabels, brazeAutoLabel }: FormatLogicalModelLabelOptions
): string => {
  // Append the thinking level (when set and not "none") to the given label. Applied to every code
  // path — resolved name and both raw-model-id fallbacks — so two values that differ only by
  // thinking level never collapse to the same label (which would hide a real change in the version
  // diff drawer).
  const withThinkingLevel = (label: string): string => {
    if (!thinkingLevel || thinkingLevel === "none") {
      return label;
    }

    return [label, thinkingLevelLabels[thinkingLevel] ?? thinkingLevel].join(" · ");
  };

  if (!model || !modelsData?.results) {
    // No model, or lookup data is missing (not yet loaded, or the models query failed) so we can't
    // resolve the display name — fall back to the raw model id (or "" when there is no model).
    return model ? withThinkingLevel(model) : "";
  }

  for (const p of modelsData.results) {
    for (const m of p.models) {
      if (m.model === model && p.code === provider) {
        const name = p.code === PROVIDERS.BRAZE && m.model === "auto" ? brazeAutoLabel : m.name;

        return withThinkingLevel(name);
      }
    }
  }

  // Lookup data is present but the provider+model combo isn't in it (e.g. a deprecated model) —
  // fall back to the raw model id, still carrying the thinking level.
  return withThinkingLevel(model);
};
